import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { formatPrice, discountPercent } from "../../utils/formatPrice";
import { mediaUrl } from "../../services/api";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { useFavorites } from "../../context/FavoritesContext";

export default function ProductCard({ product }) {
  const { addToCart, openCart } = useCart();
  const { user } = useAuth();
  const fav = useFavorites();
  const [adding, setAdding] = useState(false);

  const discount = discountPercent(product.compare_price, product.price);
  const isFav = fav?.isFavorite(product.id);

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAdding(true);
    addToCart(product, null, 1);
    toast.success(`${product.name} ajouté à votre rituel`);
    openCart();
    setTimeout(() => setAdding(false), 800);
  };

  const handleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast("Connectez-vous pour enregistrer vos favoris");
      return;
    }
    const added = await fav.toggleFavorite(product.id);
    toast.success(added ? "Ajouté à votre sélection" : "Retiré de votre sélection");
  };

  // Accent styling depending on product type
  const isVitC = product.slug?.includes("vitamine-c");
  const isHyal = product.slug?.includes("acide-hyaluronique");

  return (
    <Link
      to={`/product/${product.slug}`}
      className="group block relative bg-white rounded-2xl overflow-hidden border border-[#EAE8E4] hover:border-[#D4D0C8] hover:shadow-xl transition-all duration-500"
      data-testid={`product-card-${product.slug}`}
    >
      {/* Product Image Stage */}
      <div className="relative aspect-[4/5] bg-[#F8F8F6] overflow-hidden flex items-center justify-center p-6">
        <img
          src={mediaUrl(product.images?.[0])}
          alt={`${product.name} — The Queen`}
          className="w-full h-full object-contain mix-blend-multiply group-hover:scale-105 transition-transform duration-700 ease-out"
          loading="lazy"
        />

        {/* Badges */}
        <div className="absolute top-4 left-4 flex flex-col gap-1.5 z-10">
          {discount && (
            <span className="bg-[#111111] text-white text-[10px] tracking-[0.14em] uppercase font-body px-2.5 py-1 rounded-full">
              -{discount}
            </span>
          )}
          {isVitC && (
            <span className="bg-[#D97706]/10 text-[#D97706] text-[10px] tracking-[0.12em] uppercase font-body px-2.5 py-1 rounded-full font-medium border border-[#D97706]/20">
              15% Vit C + E
            </span>
          )}
          {isHyal && (
            <span className="bg-[#0284C7]/10 text-[#0284C7] text-[10px] tracking-[0.12em] uppercase font-body px-2.5 py-1 rounded-full font-medium border border-[#0284C7]/20">
              Multi-Moléculaire
            </span>
          )}
          {product.is_featured && !discount && !isVitC && !isHyal && (
            <span className="bg-[#111111] text-white text-[10px] tracking-[0.14em] uppercase font-body px-2.5 py-1 rounded-full">
              Coffret Royal
            </span>
          )}
        </div>

        {/* Wishlist Button */}
        <button
          onClick={handleFav}
          aria-label={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/80 backdrop-blur-md border border-[#EAE8E4] grid place-items-center text-[#111111] hover:bg-white transition-colors z-10 shadow-sm"
        >
          <Heart size={16} fill={isFav ? "#111111" : "none"} />
        </button>

        {/* Quick Add Overlay Button on Hover */}
        <div className="absolute bottom-4 inset-x-4 opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 z-10">
          <button
            onClick={handleQuickAdd}
            disabled={product.stock === 0}
            className="w-full bg-[#111111] text-white rounded-full py-2.5 px-4 text-xs font-body uppercase tracking-[0.12em] flex items-center justify-center gap-2 hover:bg-[#262626] transition-colors shadow-lg"
          >
            <ShoppingBag size={14} />
            {product.stock === 0 ? "Épuisé" : adding ? "Ajouté !" : "Ajouter au rituel"}
          </button>
        </div>
      </div>

      {/* Info Section */}
      <div className="p-5 flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between text-[11px] text-[#666666] tracking-[0.12em] uppercase font-body mb-1.5">
            <span>{product.category_name || "Sérum de Soin"}</span>
            <span>{product.volume || "30mL"}</span>
          </div>

          <h3 className="font-display text-lg text-[#111111] group-hover:text-[#444444] transition-colors leading-snug line-clamp-1">
            {product.name}
          </h3>
        </div>

        <div className="flex items-baseline justify-between mt-3 pt-3 border-t border-[#F8F8F6]">
          <div className="flex items-baseline gap-2">
            <span className="font-body text-[#111111] font-medium text-base">
              {formatPrice(product.price)}
            </span>
            {product.compare_price && (
              <span className="font-body text-[#888888] line-through text-xs">
                {formatPrice(product.compare_price)}
              </span>
            )}
          </div>

          <span className="text-xs text-[#111111] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform font-body">
            Découvrir <ArrowUpRight size={14} />
          </span>
        </div>
      </div>
    </Link>
  );
}
